<script>
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Invoice',
  metaInfo() {
    return {
      title: this.title
    };
  },
  data() {
    return {
      title: this.$t('View Invoice'),
      invoice: null,
      payment: null,
      statusCode: null
    };
  },
  computed: {
    discountAmount() {
      if (this.invoice) {
        // Calcuation per https://stripe.com/docs/sigma/subscriptions#invoice-totals-and-discounts
        return this.invoice.total - this.invoice.tax - this.invoice.subtotal;
      } else {
        return 0;
      }
    }
  },
  created() {
    // Fetch initial data
    const paymentId = this.$route.params.paymentId;
    this.$http
      .get(`/account/payments/${paymentId}`)
      .then(res => {
        this.payment = res.data.payment;
        this.invoice = res.data.invoice;
      })
      .catch(err => {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: getErrorMessage(err.response.data.code)
        });
      })
      .finally(() => (this.statusCode = 200));
  }
};
</script>

<template>
  <div v-if="payment" class="invoice">
    <!-- Header -->
    <div class="invoice-header">
      <img class="invoice-header-logo" src="@/assets/wordmark-light.svg">

      <span class="invoice-header-date">
        <strong>Invoice</strong><br>
        {{ $d(new Date(payment.invoiceDate), 'short') }}
      </span>

      <strong>Billed To</strong><br>
      {{ $store.state.user.account.organization ? $store.state.user.account.organization : $store.state.user.name }}<br>
      <span v-if="$store.state.user.account.invoiceText" class="invoice-header-invoice-text">{{ $store.state.user.account.invoiceText }}</span>
    </div>

    <!-- Body -->
    <div class="invoice-body">
      <table class="table">
        <thead>
          <tr>
            <th>Item</th>
            <th class="text-right">
Amount
</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Starting Balance</td>
            <td class="text-right">
{{ $n(invoice.starting_balance / 100, 'currency', 'en') }}
</td>
          </tr>
          <tr v-for="(line, index) in invoice.lines.data" :key="index">
            <td>{{ line.description }}</td>
            <td class="text-right">
{{ $n(line.amount / 100, 'currency', 'en') }}
</td>
          </tr>
          <tr v-if="discountAmount < 0">
            <td>
              Discount (via coupon)
            </td>
            <td class="text-right">
              {{ $n(discountAmount / 100, 'currency', 'en') }}
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td class="text-right">
{{ $n(invoice.amount_due / 100, 'currency', 'en') }}
</td>
          </tr>
        </tbody>
      </table>

      <p class="invoice-notes">
        <strong>Notes</strong>
        <br>
        This invoice was paid on {{ $d(new Date(payment.createdAt), 'short') }} using your
        {{ payment.stripeCardBrand }} ending in {{ payment.stripeCardLast4 }}.
      </p>
    </div>

    <!-- Footer -->
    <div class="invoice-footer">
      <p>
        Surreal CMS is a service of A Beautiful Site, LLC
        <br>
        <small class="text-muted">
          Built in New Hampshire &middot; Est’d 2008
          <br>
          <a href="mailto:support@surrealcms.com">support@surrealcms.com</a>
        </small>
      </p>
    </div>
  </div>
  <div v-else v-loading="!statusCode" class="invoice-loading" />
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.invoice {
  max-width: 8.5in;
  background: $--color-white;
  box-shadow: 0 0.25rem 1rem darken($--background-color-base, 7.5%);
  border-radius: 0.125rem;
  padding: 3rem;
  margin: 2rem auto;

  @include breakpoint('sm-and-down') {
    max-width: calc(100% - 1rem);
    padding: 2rem 1.5rem;
    margin: 0.5rem auto;
  }

  .invoice-header {
    line-height: 1.4;
    margin-bottom: 2rem;

    .invoice-header-logo {
      width: 100%;
      max-width: 20rem;
      margin: 0 auto 2rem auto;
      display: block;
    }

    .invoice-header-date {
      float: right;
      text-align: right;
    }

    .invoice-header-billed-to {
      margin-bottom: 1rem;
    }

    .invoice-header-more-info {
      margin-bottom: 1rem;

      @include breakpoint('md-and-up') {
        text-align: right;
      }
    }

    .invoice-header-invoice-text {
      white-space: pre-line;
    }
  }

  .invoice-body {
    min-height: 40rem; // adjust this to make the footer on invoices "fit" properly

    .table {
      border: none;
      margin-bottom: 1.5rem;
    }

    .table th,
    .table tr:last-child td {
      font-weight: bold;
      color: $--color-gray-90;
      border-bottom: none;
    }

    .invoice-notes {
      font-size: $--font-size-small;
    }
  }

  .invoice-footer {
    text-align: center;
    margin-top: 2rem;
  }
}

.invoice-loading {
  width: 100%;
  height: 100vh;
}

// Print styles
@media print {
  body {
    font-size: 14px;
    background-color: white;
    padding: 0;
    margin: 0;
  }

  .invoice {
    width: 100%;
    max-width: 100%;
    box-shadow: none;
    padding: 0;
    margin: 0;
  }
}
</style>
